import { FormEvent, useState } from 'react'
import type { RsvpWithUser, DishProgress, DishCategory } from '@fellowship/shared'
import { useOccurrenceRsvps, useApproveRsvp, useBroadcast } from '../../hooks/useChurchAdmin'
import { AdminCard } from '../admin/AdminAtoms'
import { AdmIcn } from '../admin/AdminIcons'
import { Avatar } from '../ui/Avatar'

const DISH_CATS: DishCategory[] = ['main', 'salad', 'dessert', 'drink', 'side']

const CATEGORY_LABELS: Record<DishCategory, string> = {
  main: 'Main', salad: 'Salad', dessert: 'Dessert', drink: 'Drink', side: 'Side', none: 'No dish',
}

function DishSummary({ dishProgress, onGapBroadcast }: {
  dishProgress: DishProgress
  onGapBroadcast?: (cat: DishCategory, needed: number) => void
}) {
  const goals = dishProgress.goals as Record<DishCategory, number | undefined>
  const categories = DISH_CATS.filter((cat) => (goals[cat] ?? 0) > 0)
  const gaps = categories.filter((cat) => (dishProgress.claimed[cat] ?? 0) < (goals[cat] ?? 0))

  return (
    <AdminCard title="Dish coverage" style={{ marginBottom: 20 }}>
      <div className="flex flex-wrap gap-2.5">
        {categories.map((cat) => {
          const goal = goals[cat] ?? 0
          const claimed = dishProgress.claimed[cat] ?? 0
          const full = claimed >= goal
          const needed = goal - claimed
          return (
            <div
              key={cat}
              className="px-4 py-2.5 rounded-xl text-center min-w-[72px]"
              style={{
                background: full ? '#EEF6ED' : '#FDF5E6',
                border: `1px solid ${full ? 'rgba(74,153,62,0.2)' : 'rgba(192,130,46,0.2)'}`,
              }}
            >
              <div className="text-[20px] font-bold" style={{ color: full ? '#2D6A25' : '#8C5C0A' }}>
                {claimed}/{goal}
              </div>
              <div className="text-[11px] capitalize mt-0.5" style={{ color: full ? '#2D6A25' : '#8C5C0A' }}>
                {CATEGORY_LABELS[cat]}
              </div>
              {!full && onGapBroadcast && (
                <button
                  onClick={() => onGapBroadcast(cat, needed)}
                  aria-label={`Notify attendees about ${CATEGORY_LABELS[cat].toLowerCase()} gap`}
                  className="mt-1 bg-transparent border-0 cursor-pointer text-[10px] underline underline-offset-2 p-0 font-sans"
                  style={{ color: '#8C5C0A' }}
                >
                  Notify
                </button>
              )}
            </div>
          )
        })}
      </div>
      {gaps.length > 0 && (
        <div className="mt-3 text-[12px]" style={{ color: '#8C5C0A' }}>
          Still needed:{' '}
          {gaps.map((cat) => {
            const goal = goals[cat] ?? 0
            const claimed = dishProgress.claimed[cat] ?? 0
            return `${goal - claimed} ${CATEGORY_LABELS[cat].toLowerCase()}`
          }).join(', ')}
        </div>
      )}
    </AdminCard>
  )
}

type FilterTab = 'all' | 'going' | 'maybe' | 'pending'

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'going', label: 'Going' },
  { id: 'maybe', label: 'Maybe' },
  { id: 'pending', label: 'Pending' },
]

function RsvpRow({ rsvp, onApprove, onDecline, isUpdating }: {
  rsvp: RsvpWithUser
  onApprove: () => void
  onDecline: () => void
  isUpdating: boolean
}) {
  const name = rsvp.user?.displayName ?? rsvp.guestName ?? 'Anonymous'
  const email = rsvp.user?.email ?? rsvp.guestEmail ?? ''
  const isGuest = !rsvp.userId
  const isPending = rsvp.approvalStatus === 'pending'
  const isDeclined = rsvp.approvalStatus === 'declined'

  return (
    <div
      className="px-4 py-3 rounded-[14px] bg-surface border border-line-soft flex items-start gap-3"
      style={{ opacity: isDeclined ? 0.5 : 1 }}
    >
      <Avatar name={name} src={rsvp.user?.avatarUrl} size={36} />

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
          <span className="text-[14px] font-semibold text-ink">{name}</span>
          {isGuest && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-s2 text-ink-2 border border-line-soft">
              Guest
            </span>
          )}
          {isPending && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-warn text-white">
              Pending
            </span>
          )}
          {isDeclined && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FAEBE7', color: '#7A2A1E', border: '1px solid #E9C8C0' }}>
              Declined
            </span>
          )}
        </div>
        <div className="text-[12px] text-muted mb-0.5">{email}</div>
        <div className="flex flex-wrap gap-2 text-[12px] text-ink-2">
          <span className="capitalize">{rsvp.attendanceStatus.replace('_', ' ')}</span>
          {rsvp.dishCategory !== 'none' && (
            <>
              <span>·</span>
              <span>{CATEGORY_LABELS[rsvp.dishCategory]}{rsvp.dishDescription ? `: ${rsvp.dishDescription}` : ''}</span>
            </>
          )}
          {rsvp.guestsCount > 1 && (
            <>
              <span>·</span>
              <span>+{rsvp.guestsCount - 1} guest{rsvp.guestsCount > 2 ? 's' : ''}</span>
            </>
          )}
        </div>
        {rsvp.note && (
          <div className="mt-1.5 px-2.5 py-1.5 rounded-lg bg-s2 border border-line-soft text-[12px] text-ink-2 italic">
            “{rsvp.note}”
          </div>
        )}
      </div>
      {isPending && (
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onApprove}
            disabled={isUpdating}
            className="h-8 px-3.5 rounded-full border-0 text-white text-[12px] font-semibold cursor-pointer font-sans disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: '#15803d' }}
          >
            Approve
          </button>
          <button
            onClick={onDecline}
            disabled={isUpdating}
            className="h-8 px-3.5 rounded-full bg-transparent text-[12px] font-semibold cursor-pointer font-sans disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ border: '1px solid #fca5a5', color: '#b91c1c' }}
          >
            Decline
          </button>
        </div>
      )}
    </div>
  )
}

interface OccurrenceManagementProps {
  occurrenceId: string
  canBroadcast?: boolean
  upgradeUrl?: string
}

export function OccurrenceManagement({ occurrenceId, canBroadcast = true, upgradeUrl }: OccurrenceManagementProps) {
  const { data, isLoading } = useOccurrenceRsvps(occurrenceId)
  const approveRsvp = useApproveRsvp(occurrenceId)
  const broadcast = useBroadcast(occurrenceId)

  const [filter, setFilter] = useState<FilterTab>('all')
  const [showBroadcast, setShowBroadcast] = useState(false)
  const [broadcastSubject, setBroadcastSubject] = useState('')
  const [broadcastMessage, setBroadcastMessage] = useState('')
  const [broadcastFilter, setBroadcastFilter] = useState<'all' | 'going' | 'going_maybe' | 'no_dish'>('all')
  const [broadcastSuccess, setBroadcastSuccess] = useState<number | null>(null)

  const rsvps = data?.data.rsvps ?? []
  const dishProgress = data?.data.dishProgress ?? null
  const eventTitle = data?.data.event?.title ?? 'the event'

  const pendingCount = rsvps.filter((r) => r.approvalStatus === 'pending').length
  const goingCount = rsvps.filter((r) => r.attendanceStatus === 'going' && r.approvalStatus !== 'declined').length
  const maybeCount = rsvps.filter((r) => r.attendanceStatus === 'maybe' && r.approvalStatus !== 'declined').length

  const filtered = rsvps.filter((r) => {
    if (filter === 'going') return r.attendanceStatus === 'going'
    if (filter === 'maybe') return r.attendanceStatus === 'maybe'
    if (filter === 'pending') return r.approvalStatus === 'pending'
    return true
  })

  function openGapBroadcast(cat: DishCategory, needed: number) {
    const catLabel = CATEGORY_LABELS[cat].toLowerCase()
    setBroadcastSubject(`Help needed: ${catLabel} for ${eventTitle}`)
    setBroadcastMessage(`We still need ${needed} more ${catLabel} for ${eventTitle}. Could you bring one?`)
    setBroadcastFilter('no_dish')
    setShowBroadcast(true)
  }

  function handleBroadcast(e: FormEvent) {
    e.preventDefault()
    broadcast.mutate(
      { subject: broadcastSubject, message: broadcastMessage, filter: broadcastFilter },
      {
        onSuccess: (res) => {
          setBroadcastSuccess(res.data.sent)
          setBroadcastSubject('')
          setBroadcastMessage('')
          setShowBroadcast(false)
        },
      },
    )
  }

  return (
    <>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="text-[14px] text-ink-2">
          {goingCount} going · {maybeCount} maybe{pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
        </div>
        <div className="flex gap-2.5 items-center">
          <a
            href={`/api/v1/occurrences/${occurrenceId}/rsvps/export`}
            download="guest-list.csv"
            className="h-[42px] px-4 rounded-full bg-transparent text-ink border border-line inline-flex items-center gap-1.5 text-[13px] font-medium no-underline"
          >
            <AdmIcn name="csv" size={15} /> Export CSV
          </a>
          {canBroadcast ? (
            <button
              type="button"
              onClick={() => setShowBroadcast(true)}
              className="h-[42px] px-4 rounded-full border-0 bg-brand-gradient text-white shadow-brand-glow hover:shadow-brand-glow-lg transition-shadow inline-flex items-center gap-1.5 text-[13px] font-semibold cursor-pointer font-sans"
            >
              <AdmIcn name="broadcast" size={15} /> Broadcast
            </button>
          ) : (
            <a
              href={upgradeUrl}
              className="h-[42px] px-4 rounded-full bg-s2 text-muted border border-line-soft inline-flex items-center gap-1.5 text-[13px] font-medium no-underline cursor-pointer hover:border-primary hover:text-primary transition-colors"
              title="Community plan required for broadcast emails"
            >
              <AdmIcn name="lock" size={14} /> Broadcast
            </a>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="text-[13px] text-muted mb-5">Loading…</div>
      )}

      {!isLoading && dishProgress && (
        <DishSummary dishProgress={dishProgress} onGapBroadcast={openGapBroadcast} />
      )}

      {broadcastSuccess !== null && (
        <div
          className="px-4 py-3 rounded-xl mb-4 text-[13px] flex items-center justify-between"
          style={{ background: '#EEF6ED', border: '1px solid rgba(74,153,62,0.2)' }}
        >
          <span style={{ color: '#2D6A25' }}>
            Broadcast sent to {broadcastSuccess} attendee{broadcastSuccess !== 1 ? 's' : ''}.
          </span>
          <button
            onClick={() => setBroadcastSuccess(null)}
            className="bg-transparent border-0 cursor-pointer text-[12px] underline underline-offset-2 font-sans"
            style={{ color: '#2D6A25' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {showBroadcast && (
        <AdminCard title="Broadcast to attendees" style={{ marginBottom: 20 }}>
          <form onSubmit={handleBroadcast}>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                value={broadcastSubject}
                onChange={(e) => setBroadcastSubject(e.target.value)}
                placeholder="Subject"
                required
                aria-label="Broadcast subject"
                className="input"
              />
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Message…"
                required
                rows={4}
                aria-label="Broadcast message"
                className="input h-auto py-3 resize-y leading-relaxed"
              />
              <div className="flex items-center justify-between flex-wrap gap-2.5">
                <select
                  value={broadcastFilter}
                  onChange={(e) => setBroadcastFilter(e.target.value as typeof broadcastFilter)}
                  aria-label="Send to"
                  className="h-[38px] px-3 rounded-[10px] bg-s2 border border-line text-[13px] font-sans text-ink outline-none cursor-pointer"
                >
                  <option value="all">All attendees</option>
                  <option value="going_maybe">Going + maybe</option>
                  <option value="going">Going only</option>
                  {dishProgress && <option value="no_dish">No dish commitment</option>}
                </select>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowBroadcast(false)}
                    className="h-[38px] px-4 rounded-full bg-transparent text-ink border border-line text-[13px] font-medium cursor-pointer font-sans"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={broadcast.isPending}
                    className="h-[38px] px-4 rounded-full border-0 bg-brand-gradient text-white shadow-brand-glow text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none"
                  >
                    {broadcast.isPending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </AdminCard>
      )}

      <div className="mb-3.5 flex gap-1">
        {FILTER_TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`h-[34px] px-3.5 rounded-full border-0 text-[12.5px] cursor-pointer font-sans ${filter === id ? 'bg-primary-soft text-primary font-semibold' : 'bg-s2 text-ink-2 font-medium'}`}
          >
            {label}
            {id === 'pending' && pendingCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary text-white text-[10px] font-bold">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="px-6 py-12 rounded-[18px] text-center bg-surface border border-line-soft">
          <p className="m-0 text-[13px] text-muted">
            {filter === 'pending' ? 'No pending approvals.' : 'No attendees in this view.'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((rsvp) => (
            <RsvpRow
              key={rsvp.id}
              rsvp={rsvp}
              onApprove={() => approveRsvp.mutate({ rsvpId: rsvp.id, approvalStatus: 'approved' })}
              onDecline={() => approveRsvp.mutate({ rsvpId: rsvp.id, approvalStatus: 'declined' })}
              isUpdating={approveRsvp.isPending}
            />
          ))}
        </div>
      )}
    </>
  )
}
